import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ClipLoader } from 'react-spinners';
import Swal from 'sweetalert2';
import { Col, Row, Radio, Button } from 'antd';
import { useFormik } from 'formik';
import { IoIosArrowForward, IoMdHelpCircle } from 'react-icons/io';
import history from '~/services/history';

import { useDispatch, useSelector } from 'react-redux';
import * as actions from '~/store/modules/careLines/actions';

import {
  conveniosPaciente,
  dadosDaDoenca,
  inflamatorio,
  pesquisaLinfonodo,
  reconstrucaoMamaria,
  comorbidades,
  tratamento,
  naoRealizacaoMama,
  dadosDaSaida,
  laudoBiopsia,
  laudoCirurgia,
  estadiamentoClinico,
} from './options';

import Breadcumbs from '~/components/Breadcumbs';
import ErrorField from '~/components/ErrorField';

import { LcCancerDeMamaSchema } from '~/utils/schemas/lcCancerDeMamaSchema';
import {
  numbersAndComa,
  onlyNumbers,
  scrollPageTo,
} from '~/helpers/generalFunctions';

import {
  PageTitle,
  BoxContent,
  Content,
  TitleSection,
  BoxInput,
  Label,
  BoxRadios,
  ButtonBox,
  BoxForm,
} from '../styles/style';

import DadoFaltantes from '../components/DadosFaltantes';

import { Input } from '~/components/Input';
import { RadioInput, RadioInputFlex } from '../components/RadioInput';

import { modalConfirm } from '../../LinhasDeCuidadoFormulario/dados/optionsModal';
import { toast } from 'react-toastify';
import ButtonReset from '../components/ButtonReset';
import ButtonBack from '~/components/ButtonBack';
import ExceptionsForm from '../components/ExceptionsForm';
import { getTitleIndicador } from '../careLinesInfo';

const FichadeColetaCancerDeMama = ({ location }) => {
  const dispath = useDispatch();

  const { id } = location?.state;

  const { careLineData, loading, errorData } = useSelector(
    state => state.careLines
  );

  const firstFocus = useRef(null);
  const [biopsia, setBiopsia] = useState(false);
  const [reconstrucaoMama, setReconstrucaoMama] = useState(false);
  const [numeroPaciente, setNumeroPaciente] = useState('');

  const [dados, setDados] = useState(null);

  // Limpar errors
  useEffect(() => {
    dispath(
      actions.cancerDeMamaFailure({
        errorData: null,
      })
    );
  }, []);

  useEffect(() => {
    if (location?.state?.id) {
      dispath(
        actions.cancerDeMamaGetRequest({
          id: location?.state?.id,
        })
      );
    }
  }, [location?.state]);

  useEffect(() => {
    if (id && careLineData) {
      setDados(careLineData);
    }
  }, [careLineData]);

  const exibeLaudo = useCallback((name, value) => {
    if (name == 'biopsia_mama') {
      if (value == '1') {
        setBiopsia(true);
      } else {
        setBiopsia(false);
        formik.setFieldValue('focos_biopsia_mama', null);
        formik.setFieldValue('margens_bipsia_mama', null);
        formik.setFieldValue('invasao_linfo_biopsia_mama', null);
        formik.setFieldValue('expressao_biopsia_mama', null);
      }
      formik.setFieldValue(`${name}`, value);
    }
  }, []);

  const exibeTratamento = useCallback((name, value) => {
    if (name == 'reconstrucao_mama' && value == '0') {
      setReconstrucaoMama(true);
    } else {
      setReconstrucaoMama(false);
      formik.setFieldValue('nao_realizacao_mama', null);
    }
    formik.setFieldValue(`${name}`, value);
  }, []);

  useEffect(() => {
    if (dados?.biopsia_mama == '1') {
      setBiopsia(true);
    }
    if (dados?.reconstrucao_mama == '0') {
      setReconstrucaoMama(true);
    }
  }, [dados]);

  // Reset Button
  const [isLoaded, setIsloaded] = useState(false);

  const handleResetGroup = useCallback(group => {
    group.map(item => {
      formik.setFieldValue(`${item.name}`, null);
      exibeLaudo(`${item.name}`, null);
      exibeTratamento(`${item.name}`, null);
    });
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setIsloaded(true);
    }, 2000);
  }, []);

  // End Reset Button

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      num_registro_mama: dados?.num_registro_mama?.toString() || null,
      dn_mama: dados?.dn_mama?.toString() || null,
      convenio_mama: dados?.convenio_mama?.toString() || null,
      peso_mama: dados?.peso_mama?.toString() || null,
      altura_mama: dados?.altura_mama,
      imc_mama: null,
      avc_mama: dados?.avc_mama?.toString() || null,
      iam_mama: dados?.iam_mama?.toString() || null,
      dm_mama: dados?.dm_mama?.toString() || null,
      autoimune_mama: dados?.autoimune_mama?.toString() || null,
      tabagismo_mama: dados?.tabagismo_mama?.toString() || null,
      metastase_mama: dados?.metastase_mama?.toString() || null,
      ca_inflamatorio_mama: dados?.ca_inflamatorio_mama?.toString() || null,
      biopsia_mama: dados?.biopsia_mama?.toString() || null,
      focos_biopsia_mama: dados?.focos_biopsia_mama?.toString() || null,
      margens_bipsia_mama: dados?.margens_bipsia_mama?.toString() || null,
      invasao_linfo_biopsia_mama:
        dados?.invasao_linfo_biopsia_mama?.toString() || null,
      expressao_biopsia_mama: dados?.expressao_biopsia_mama?.toString() || null,
      estad_clinico_mama: dados?.estad_clinico_mama?.toString() || null,
      entrada_data_mama: dados?.entrada_data_mama?.toString() || null,
      entrada_hora_mama: dados?.entrada_hora_mama?.toString() || null,
      cirurgia_data_mama: dados?.cirurgia_data_mama?.toString() || null,
      cirurgia_tipo_mama: dados?.cirurgia_tipo_mama?.toString() || null,
      sentinela_mama: dados?.sentinela_mama?.toString() || null,
      reconstrucao_mama: dados?.reconstrucao_mama?.toString() || null,
      nao_realizacao_mama: dados?.nao_realizacao_mama?.toString() || null,
      focos_cirurgia_mama: dados?.focos_cirurgia_mama?.toString() || null,
      linfonodos_cirurgia_mama:
        dados?.linfonodos_cirurgia_mama?.toString() || null,
      grau_tumor_mama: dados?.grau_tumor_mama?.toString() || null,
      margens_cirurgia_mama: dados?.margens_cirurgia_mama?.toString() || null,
      invasao_linfo_cirurgia_mama:
        dados?.invasao_linfo_cirurgia_mama?.toString() || null,
      expressao_cirurgia_mama:
        dados?.expressao_cirurgia_mama?.toString() || null,
      tnm_pos_mama: dados?.tnm_pos_mama?.toString() || null,
      covid_mama: dados?.covid_mama?.toString() || null,
      saida_motivo_mama: dados?.saida_motivo_mama?.toString() || null,
      saida_data_mama: dados?.saida_data_mama?.toString() || null,
      saida_hora_mama: dados?.saida_hora_mama?.toString() || null,
    },
    validationSchema: LcCancerDeMamaSchema,

    onSubmit: async values => {
      if (id) {
        const payload = {
          ...dados,
          ...values,
        };

        // limpa erros
        dispath(
          actions.cancerDeMamaFailure({
            errorData: null,
          })
        );

        return dispath(
          actions.cancerDeMamaUpdateRequest({
            data: payload,
            id: id,
            periodo: location?.state?.periodo,
          })
        );
      }
      dispath(
        actions.cancerDeMamaRequest({
          data: values,
          periodo: location?.state?.periodo,
        })
      );
    },
  });

  const callHandleSubmit = () => {
    Swal.fire(modalConfirm).then(result => {
      if (result.value) {
        formik.handleSubmit();
      }
    });
  };

  useEffect(() => {
    scrollPageTo(75);
  }, []);

  useEffect(() => {
    const keyErrors = Object.keys(formik.errors);
    if (keyErrors.length > 0 && formik.isSubmitting) {
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${keyErrors[0]}"]`);
        if (errorElement)
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [formik.submitCount, formik.errors]);

  const getErrosPdf = useCallback(() => {
    const { indicador } = location.state;
    history.push({
      pathname: `/area-de-suporte`,
      state: {
        searchBy: `${getTitleIndicador(indicador)}`,
      },
    });
  }, []);

  return (
    <>
      <Breadcumbs
        path="cancer_de_mama"
        pathTitle="Adicionar Indicadores de Linhas de Cuidado / Câncer de Mama"
      />
      <PageTitle>
        <div>
          <ButtonBack />
          <span ref={firstFocus}>Ficha de coleta</span>
        </div>

        {errorData?.fields && isLoaded && (
          <button className="errorButton" type="button" onClick={getErrosPdf}>
            <IoMdHelpCircle size={16} color="#FFF" />
            Documentação de erros
          </button>
        )}
      </PageTitle>

      {loading ? (
        <BoxContent>
          <div className="boxSpin">
            <ClipLoader size={50} color="#74a50a" loading={loading} />
          </div>
        </BoxContent>
      ) : (
        <BoxContent>
          <div className="title">
            <h2>Preencha abaixo as informações:</h2>
          </div>

          <div className="BoxErros">
            {errorData?.fields && (
              <ExceptionsForm
                lc="cancer_de_mama"
                exceptions={errorData?.fields}
              />
            )}
          </div>

          <Content>
            <form onSubmit={formik.handleSubmit} method="post">
              {/* Linha de Cuidado, Competência e Dados Gerais */}

              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <BoxInput>
                        <Col span={12} className="item">
                          <TitleSection className="font-green">
                            {'>'} Linha de Cuidado
                          </TitleSection>
                          <input
                            type="text"
                            name="indicadorNome"
                            defaultValue={location?.state?.indicadorNome || ''}
                            disabled
                          />
                        </Col>
                        <Col span={12} className="item">
                          <TitleSection className="font-green">
                            {'>'} Competência
                          </TitleSection>
                          <input
                            type="text"
                            name="periodo"
                            defaultValue={location?.state?.periodo || ''}
                            disabled
                          />
                        </Col>
                      </BoxInput>

                      <hr />

                      <TitleSection>
                        {'>'} Dados Gerais da paciente
                      </TitleSection>

                      <Row>
                        <Col span={12} className="item">
                          <Input
                            name="num_registro_mama"
                            label="1) Número do registro"
                            type="text"
                            maxLength={5}
                            value={
                              formik.values.num_registro_mama || numeroPaciente
                            }
                            hasError={
                              formik.errors.num_registro_mama &&
                              formik.touched.num_registro_mama
                                ? formik.errors.num_registro_mama
                                : ''
                            }
                            onChange={({ target }) => {
                              formik.setFieldValue(
                                'num_registro_mama',
                                onlyNumbers(target.value)
                              );
                            }}
                          />
                        </Col>

                        <Col span={12} className="item">
                          <DadoFaltantes
                            title="2) Data de nascimento"
                            name="dn_mama"
                            formik={formik}
                            hasError={
                              formik.errors.dn_mama && formik.submitCount > 0
                                ? formik.errors.dn_mama
                                : ''
                            }
                          />
                        </Col>
                      </Row>

                      <Col span={24}>
                        <Row>
                          <Col span={12} className="item">
                            {conveniosPaciente.map(item => (
                              <RadioInputFlex
                                key={`${item.id}-${item.name}`}
                                item={item}
                                formik={formik}
                              />
                            ))}
                          </Col>
                          <Col span={12} className="item">
                            <Input
                              name="peso_mama"
                              label="4) Peso (Kg)"
                              type="text"
                              // placeholder="Peso (Kg)"
                              value={formik.values.peso_mama}
                              hasError={
                                (formik.errors.peso_mama &&
                                  formik.touched.peso_mama) ||
                                formik.submitCount > 0
                                  ? formik.errors.peso_mama
                                  : ''
                              }
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'peso_mama',
                                  numbersAndComa(target.value)
                                );
                              }}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row>
                          <Col span={12} className="item">
                            <Input
                              name="altura_mama"
                              label="5) Altura (cm)"
                              type="text"
                              // placeholder="Altura (cm)"
                              value={formik.values.altura_mama}
                              hasError={
                                formik.errors.altura_mama &&
                                formik.submitCount > 0
                                  ? formik.errors.altura_mama
                                  : ''
                              }
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'altura_mama',
                                  onlyNumbers(target.value)
                                );
                              }}
                            />
                          </Col>

                          <Col span={12} className="item">
                            <Input
                              name="imc_mama"
                              label="6) Índice de Massa Corporal"
                              type="text"
                              disabled
                              // placeholder="Índice de Massa Corporal"
                              value={
                                formik.values.peso_mama &&
                                formik.values.altura_mama &&
                                (
                                  (formik.values?.peso_mama?.replace(',', '.') /
                                    formik.values.altura_mama ** 2) *
                                    10000 || 0
                                ).toFixed(2)
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                {/* aqui */}
              </BoxForm>
              <hr />
              {/* Dados específicos da linha de cuidado */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <h4>7{')'} Comorbidades</h4>
                      </Col>
                      <Col span={12}>
                        {isLoaded && (
                          <ButtonReset
                            resetGroup={comorbidades}
                            handleResetGroup={handleResetGroup}
                          />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      {comorbidades.map(item => (
                        <Col span={12} style={{ height: '160px' }}>
                          <div className="containerRadios">
                            <RadioInput
                              key={`${item.id}-${item.name}`}
                              item={item}
                              formik={formik}
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <TitleSection>{'>'} Dados da doença</TitleSection>
                      </Col>
                    </Row>
                    {dadosDaDoenca.map(item => (
                      <Row>
                        <Row>
                          <Col span={12}>
                            <Label>{item.label}</Label>
                          </Col>
                          <Col span={12}>
                            {isLoaded && (
                              <ButtonReset
                                resetGroup={[...dadosDaDoenca]}
                                handleResetGroup={handleResetGroup}
                              />
                            )}
                          </Col>
                        </Row>
                        <BoxInput key={item.id}>
                          {item.opcoes.map(opcao => (
                            <Col key={opcao.id}>
                              <BoxRadios
                                active={
                                  formik.values[item.name] === opcao.value
                                }
                                style={{ maxWidth: '100%' }}
                              >
                                <Radio
                                  name={item.name}
                                  value={formik.values[opcao.value]}
                                  checked={
                                    formik.values[item.name] === opcao.value
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `${item.name}`,
                                      opcao.value
                                    )
                                  }
                                >
                                  {opcao.descricao}
                                </Radio>
                              </BoxRadios>
                            </Col>
                          ))}
                        </BoxInput>

                        {formik.errors[item.name] && formik.submitCount > 0 ? (
                          <ErrorField message={formik.errors[item.name]} />
                        ) : null}
                      </Row>
                    ))}
                    {inflamatorio.map(item => (
                      <Row>
                        <Row>
                          <Col span={12}>
                            <Label>{item.label}</Label>
                          </Col>
                          <Col span={12}>
                            {isLoaded && (
                              <ButtonReset
                                resetGroup={[...inflamatorio]}
                                handleResetGroup={handleResetGroup}
                              />
                            )}
                          </Col>
                        </Row>
                        <BoxInput key={item.id}>
                          {item.opcoes.map(opcao => (
                            <Col key={opcao.id}>
                              <BoxRadios
                                active={
                                  formik.values[item.name] === opcao.value
                                }
                                style={{ maxWidth: '100%' }}
                              >
                                <Radio
                                  name={item.name}
                                  value={formik.values[opcao.value]}
                                  checked={
                                    formik.values[item.name] === opcao.value
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `${item.name}`,
                                      opcao.value
                                    )
                                  }
                                >
                                  {opcao.descricao}
                                </Radio>
                              </BoxRadios>
                            </Col>
                          ))}
                        </BoxInput>

                        {formik.errors[item.name] && formik.submitCount > 0 ? (
                          <ErrorField message={formik.errors[item.name]} />
                        ) : null}
                      </Row>
                    ))}
                  </Col>
                </Row>
              </BoxForm>

              <hr />
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Row>
                        <Col span={12}>
                          <Label>
                            10) Paciente realizou biópsia de fragmento{' '}
                            <i>(core biopsy)</i> antes da cirurgia de mama
                          </Label>
                        </Col>
                        <Col span={12}>
                          {isLoaded && (
                            <ButtonReset
                              resetGroup={[{ name: 'biopsia_mama' }]}
                              handleResetGroup={handleResetGroup}
                            />
                          )}
                        </Col>
                      </Row>

                      <Row>
                        <Col span={4}>
                          <BoxRadios
                            active={formik.values.biopsia_mama === '1'}
                          >
                            <Radio
                              name="biopsia_mama"
                              value={formik.values.biopsia_mama}
                              checked={formik.values.biopsia_mama === '1'}
                              onChange={() => exibeLaudo('biopsia_mama', '1')}
                            >
                              Sim
                            </Radio>
                          </BoxRadios>
                        </Col>

                        <Col span={4}>
                          <BoxRadios
                            active={formik.values.biopsia_mama === '0'}
                          >
                            <Radio
                              name="biopsia_mama"
                              value={formik.values.biopsia_mama}
                              checked={formik.values.biopsia_mama === '0'}
                              onChange={() => exibeLaudo('biopsia_mama', '0')}
                            >
                              Não
                            </Radio>
                          </BoxRadios>
                        </Col>
                      </Row>

                      {formik.errors.biopsia_mama && formik.submitCount > 0 ? (
                        <ErrorField message={formik.errors.biopsia_mama} />
                      ) : null}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>

              <hr />

              {/* Laudo anatomopatológico da biópsia */}
              {biopsia && (
                <>
                  <BoxForm>
                    <Row>
                      <Col span={24}>
                        <Row>
                          <Col span={12}>
                            <h4>
                              11{')'} Itens presentes no laudo da biópsia de
                              fragmento
                            </h4>
                          </Col>
                          <Col span={12}>
                            {isLoaded && (
                              <ButtonReset
                                resetGroup={laudoBiopsia}
                                handleResetGroup={handleResetGroup}
                              />
                            )}
                          </Col>
                        </Row>

                        <div
                          className="inlineRadios"
                          style={{ flexWrap: 'wrap' }}
                        >
                          {laudoBiopsia.map(item => (
                            <>
                              <BoxInput key={item.id}>
                                <Row>
                                  <Label>{item.label}</Label>
                                  {item.itens &&
                                    item.itens.map(linha => (
                                      <Label
                                        style={{
                                          marginLeft: '10px',
                                          color: '#898989',
                                        }}
                                        key={linha.id}
                                      >
                                        {linha.descricao}
                                        <br />
                                      </Label>
                                    ))}
                                  <div className="inlineRadiosInput">
                                    {item.opcoes.map(opcao => (
                                      <BoxRadios
                                        key={opcao.id}
                                        style={{
                                          maxWidth: '150px',
                                        }}
                                        active={
                                          formik.values[item.name] ===
                                          opcao.value
                                        }
                                      >
                                        <Radio
                                          name={item.name}
                                          value={formik.values[opcao.value]}
                                          checked={
                                            formik.values[item.name] ===
                                            opcao.value
                                          }
                                          onChange={() =>
                                            formik.setFieldValue(
                                              `${item.name}`,
                                              opcao.value
                                            )
                                          }
                                        >
                                          {opcao.descricao}
                                        </Radio>
                                      </BoxRadios>
                                    ))}
                                  </div>
                                  {formik.errors[item.name] &&
                                  formik.submitCount > 0 ? (
                                    <ErrorField
                                      message={formik.errors[item.name]}
                                    />
                                  ) : null}
                                </Row>
                              </BoxInput>
                            </>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </BoxForm>
                  <hr />
                </>
              )}

              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      {isLoaded && (
                        <ButtonReset
                          resetGroup={estadiamentoClinico}
                          handleResetGroup={handleResetGroup}
                        />
                      )}
                    </Row>
                    {estadiamentoClinico.map(item => (
                      <Row>
                        <Label>{item.label}</Label>
                        <BoxInput key={item.id}>
                          {item.opcoes.map(opcao => (
                            <Col key={opcao.id}>
                              <BoxRadios
                                active={
                                  formik.values[item.name] === opcao.value
                                }
                                style={{ maxWidth: '100%' }}
                              >
                                <Radio
                                  name={item.name}
                                  value={formik.values[opcao.value]}
                                  checked={
                                    formik.values[item.name] === opcao.value
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `${item.name}`,
                                      opcao.value
                                    )
                                  }
                                >
                                  {opcao.descricao}
                                </Radio>
                              </BoxRadios>
                            </Col>
                          ))}
                        </BoxInput>

                        {formik.errors[item.name] && formik.submitCount > 0 ? (
                          <ErrorField message={formik.errors[item.name]} />
                        ) : null}
                      </Row>
                    ))}
                  </Col>
                </Row>
              </BoxForm>
              {/* Inicio seção Dados da Doença e tratamento */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        13{')'} Data e hora da entrada da paciente na
                        instituição
                      </h4>
                      <Col span={12} className="item">
                        <DadoFaltantes
                          title="Data da chegada na instituição"
                          name="entrada_data_mama"
                          formik={formik}
                          errors={formik.errors}
                          hasError={
                            formik.errors.entrada_data_mama &&
                            formik.submitCount > 0
                              ? formik.errors.entrada_data_mama
                              : ''
                          }
                        />
                      </Col>
                      <Col span={12} className="item">
                        <DadoFaltantes
                          title="Hora da chegada na instituição"
                          name="entrada_hora_mama"
                          formik={formik}
                          errors={formik.errors}
                          hasError={
                            formik.errors.entrada_hora_mama &&
                            formik.submitCount > 0
                              ? formik.errors.entrada_hora_mama
                              : ''
                          }
                        />
                      </Col>
                    </Row>
                  </Col>

                  <Col span={24}>
                    <Row>
                      <Col span={12} className="item">
                        <DadoFaltantes
                          title="14) Data do procedimento cirúrgico"
                          name="cirurgia_data_mama"
                          formik={formik}
                          errors={formik.errors}
                          hasError={
                            formik.errors.cirurgia_data_mama &&
                            formik.submitCount > 0
                              ? formik.errors.cirurgia_data_mama
                              : ''
                          }
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24} className="item">
                        {tratamento.map(item => (
                          <Col span={24} key={item.id}>
                            <BoxInput>
                              <Row>
                                <Row>
                                  <Col span={12}>
                                    <Label>{item.label}</Label>
                                  </Col>
                                  <Col span={12}>
                                    {isLoaded && (
                                      <ButtonReset
                                        resetGroup={tratamento}
                                        handleResetGroup={handleResetGroup}
                                      />
                                    )}
                                  </Col>
                                </Row>
                                <div className="inlineRadiosBox">
                                  {item.opcoes.map(opcao => (
                                    <Col span={12} key={opcao.id}>
                                      <BoxRadios
                                        className="v-large"
                                        active={
                                          formik.values[item.name] ===
                                          opcao.value
                                        }
                                      >
                                        <Radio
                                          name={item.name}
                                          value={formik.values[opcao.value]}
                                          checked={
                                            formik.values[item.name] ===
                                            opcao.value
                                          }
                                          onChange={() =>
                                            exibeTratamento(
                                              item.name,
                                              opcao.value
                                            )
                                          }
                                        >
                                          {opcao.descricao}{' '}
                                        </Radio>
                                      </BoxRadios>
                                      {formik.errors[item.name] &&
                                      formik.submitCount > 0 ? (
                                        <ErrorField
                                          message={formik.errors[item.name]}
                                        />
                                      ) : null}
                                    </Col>
                                  ))}
                                </div>
                              </Row>
                            </BoxInput>
                          </Col>
                        ))}
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24} className="item">
                        {pesquisaLinfonodo.map(item => (
                          <Col span={24} key={item.id}>
                            <BoxInput>
                              <Row style={{ width: '900px' }}>
                                <Col span={12}>
                                  <Label>{item.label}</Label>
                                </Col>
                                <Col span={12}>
                                  {isLoaded && (
                                    <ButtonReset
                                      resetGroup={pesquisaLinfonodo}
                                      handleResetGroup={handleResetGroup}
                                    />
                                  )}
                                </Col>
                              </Row>
                              <div className="inlineRadiosBox">
                                {item.opcoes.map(opcao => (
                                  <Col span={12} key={opcao.id}>
                                    <BoxRadios
                                      className="v-large"
                                      active={
                                        formik.values[item.name] === opcao.value
                                      }
                                    >
                                      <Radio
                                        name={item.name}
                                        value={formik.values[opcao.value]}
                                        checked={
                                          formik.values[item.name] ===
                                          opcao.value
                                        }
                                        onChange={() =>
                                          exibeTratamento(
                                            item.name,
                                            opcao.value
                                          )
                                        }
                                      >
                                        {opcao.descricao}{' '}
                                      </Radio>
                                    </BoxRadios>
                                    {formik.errors[item.name] &&
                                    formik.submitCount > 0 ? (
                                      <ErrorField
                                        message={formik.errors[item.name]}
                                      />
                                    ) : null}
                                  </Col>
                                ))}
                              </div>
                            </BoxInput>
                          </Col>
                        ))}
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24} className="item">
                        {reconstrucaoMamaria.map(item => (
                          <Col span={24} key={item.id}>
                            <BoxInput>
                              <Row style={{ width: '900px' }}>
                                <Col span={12}>
                                  <Label>{item.label}</Label>
                                </Col>
                                <Col span={12}>
                                  {isLoaded && (
                                    <ButtonReset
                                      resetGroup={reconstrucaoMamaria}
                                      handleResetGroup={handleResetGroup}
                                    />
                                  )}
                                </Col>
                              </Row>
                              <div className="inlineRadiosBox">
                                {item.opcoes.map(opcao => (
                                  <Col span={12} key={opcao.id}>
                                    <BoxRadios
                                      className="v-large"
                                      active={
                                        formik.values[item.name] === opcao.value
                                      }
                                    >
                                      <Radio
                                        name={item.name}
                                        value={formik.values[opcao.value]}
                                        checked={
                                          formik.values[item.name] ===
                                          opcao.value
                                        }
                                        onChange={() =>
                                          exibeTratamento(
                                            item.name,
                                            opcao.value
                                          )
                                        }
                                      >
                                        {opcao.descricao}{' '}
                                      </Radio>
                                    </BoxRadios>
                                    {formik.errors[item.name] &&
                                    formik.submitCount > 0 ? (
                                      <ErrorField
                                        message={formik.errors[item.name]}
                                      />
                                    ) : null}
                                  </Col>
                                ))}
                              </div>
                            </BoxInput>
                          </Col>
                        ))}
                      </Col>
                    </Row>

                    {reconstrucaoMama && (
                      <Row>
                        <Col span={24} className="item">
                          {naoRealizacaoMama.map(item => (
                            <RadioInput
                              key={`${item.id}-${item.name}`}
                              item={item}
                              formik={formik}
                            />
                          ))}
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </BoxForm>
              <hr />
              {/* Fim da seção */}

              {/* Laudo anatomopatológico da cirurgia */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <h4>
                          19{')'} Itens presentes no laudo anatomopatológico da
                          cirurgia de mama
                        </h4>
                      </Col>
                      <Col span={12}>
                        {isLoaded && (
                          <ButtonReset
                            resetGroup={laudoCirurgia}
                            handleResetGroup={handleResetGroup}
                          />
                        )}
                      </Col>
                    </Row>
                    {/* <Row>
                      <Col span={24} className="item">
                        {laudoCirurgia.map(item => (
                          <RadioInput
                            key={`${item.id}-${item.name}`}
                            item={item}
                            formik={formik}
                            wideLabel
                          />
                        ))}
                      </Col>
                    </Row> */}

                    <div className="inlineRadios" style={{ flexWrap: 'wrap' }}>
                      {laudoCirurgia.map(item => (
                        <>
                          <BoxInput key={item.id}>
                            <Row>
                              <Label>{item.label}</Label>
                              {item.itens &&
                                item.itens.map(linha => (
                                  <Label
                                    style={{
                                      marginLeft: '10px',
                                      color: '#898989',
                                    }}
                                    key={linha.id}
                                  >
                                    {linha.descricao}
                                    <br />
                                  </Label>
                                ))}
                              <div className="inlineRadiosInput">
                                {item.opcoes.map(opcao => (
                                  <BoxRadios
                                    key={opcao.id}
                                    style={{
                                      maxWidth: '150px',
                                    }}
                                    active={
                                      formik.values[item.name] === opcao.value
                                    }
                                  >
                                    <Radio
                                      name={item.name}
                                      value={formik.values[opcao.value]}
                                      checked={
                                        formik.values[item.name] === opcao.value
                                      }
                                      onChange={() =>
                                        formik.setFieldValue(
                                          `${item.name}`,
                                          opcao.value
                                        )
                                      }
                                    >
                                      {opcao.descricao}
                                    </Radio>
                                  </BoxRadios>
                                ))}
                              </div>
                              {formik.errors[item.name] &&
                              formik.submitCount > 0 ? (
                                <ErrorField
                                  message={formik.errors[item.name]}
                                />
                              ) : null}
                            </Row>
                          </BoxInput>
                        </>
                      ))}
                    </div>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Inicio seção Dados da Saída */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <TitleSection>{'>'} Dados da Saída</TitleSection>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24} className="item">
                        {dadosDaSaida.map(item => (
                          <RadioInput
                            key={`${item.id}-${item.name}`}
                            item={item}
                            reset
                            formik={formik}
                          />
                        ))}
                      </Col>
                    </Row>

                    <Row>
                      <h4>23{')'} Data e hora da saída</h4>
                    </Row>
                    <Col span={12} className="item">
                      <DadoFaltantes
                        title="Data da saída hospitalar"
                        name="saida_data_mama"
                        formik={formik}
                        hasError={
                          formik.errors.saida_data_mama &&
                          formik.submitCount > 0
                            ? formik.errors.saida_data_mama
                            : ''
                        }
                      />
                    </Col>

                    <Col span={12} className="item">
                      <DadoFaltantes
                        title="Hora da saída hospitalar"
                        name="saida_hora_mama"
                        formik={formik}
                        hasError={
                          formik.errors.saida_hora_mama &&
                          formik.submitCount > 0
                            ? formik.errors.saida_hora_mama
                            : ''
                        }
                      />
                    </Col>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              <ButtonBox>
                <button type="button" onClick={callHandleSubmit}>
                  {loading ? 'Enviando dados...' : 'Salvar'}
                </button>
              </ButtonBox>
            </form>
          </Content>
        </BoxContent>
      )}
    </>
  );
};

FichadeColetaCancerDeMama.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      indicador: PropTypes.string,
      indicadorNome: PropTypes.string,
      periodo: PropTypes.string,
    }),
  }).isRequired,
};

export default FichadeColetaCancerDeMama;
